/**
 * EntityPageLayout Component
 *
 * A standardized layout for entity detail pages with consistent padding and loading states.
 * Responsive design with reduced padding on mobile.
 */

import React from 'react';
import { Box, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { colors, spacing } from '../../tokens';

export interface EntityPageLayoutProps {
    /** Page content */
    children: React.ReactNode;
    /** Whether content is loading */
    loading?: boolean;
    /** Additional styles */
    sx?: SxProps<Theme>;
}

export const EntityPageLayout: React.FC<EntityPageLayoutProps> = ({ children, loading = false, sx }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 400,
                    width: '100%',
                    backgroundColor: colors.neutral[100],
                    flex: 1,
                }}>
                <CircularProgress size={48} sx={{ color: colors.primary[500] }} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: isMobile ? 2 : 3,
                p: isMobile ? 2 : 3,
                pb: isMobile ? 4 : 6, // Extra bottom padding for scroll comfort
                backgroundColor: colors.neutral[100],
                boxSizing: 'border-box',
                // Let content determine height - parent handles scrolling
                width: '100%',
                ...sx,
            }}>
            {children}
        </Box>
    );
};

export default EntityPageLayout;
